<!-- @format -->
<template>
    <div class="collateral-edit-widget">
        <div class="widget-header">
            <div class="title-container">
                <h3>{{ steps[step].title }}</h3>
            </div>
            <div class="svg-container widget-close-button" @click="closeWidget">
                <widget-close-svg />
            </div>
        </div>
        <div class="content-wrapper">
            <div v-if="step === 'edit'" class="step edit-step">
                <div class="form-row">
                    <img
                        v-if="collateral.previewImageUrl"
                        class="preview-image"
                        :src="collateral.previewImageUrl"
                        alt="preview image"
                    />
                    <app-file-icon v-else :type="collateral.fileType" />
                </div>
                <div class="form-row">
                    <app-input v-model="collateral.name" label="Name" />
                </div>
                <div class="form-row">
                    <global-category-dropdown
                        :account-id="accountId"
                        :selected="collateral.categories"
                        show-selected-up
                        @input="addListCategories($event)"
                        @remove="deleteCategory"
                    />
                </div>
            </div>
            <div v-else-if="step === 'saving'" class="step saving-step">
                <quick-add-saving-svg />
            </div>
        </div>
        <div class="widget-footer">
            <div class="cancel-container">
                <app-button class="transparent-btn" @click="closeWidget"> Cancel </app-button>
            </div>
            <div class="action-container">
                <app-button :loading="steps[step].loading" @click="steps[step].action">
                    {{ steps[step].buttonText }}
                </app-button>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button');
const AppInput = () => import('@/components/common/app-input');
const AppFileIcon = () => import('@/components/common/app-file-icon');
const QuickAddSavingSvg = () => import('@/components/svg/quick-add/quick-add-saving-svg');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const GlobalCategoryDropdown = () => import('@/components/common/global-category-dropdown');

import EventBus from '@/scripts/event-bus.js';

export default {
    name: 'collateral-edit-widget',

    components: {
        'app-button': AppButton,
        'app-input': AppInput,
        'app-file-icon': AppFileIcon,
        'quick-add-saving-svg': QuickAddSavingSvg,
        'widget-close-svg': WidgetCloseSvg,
        'global-category-dropdown': GlobalCategoryDropdown,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            isInitializing: true,

            collateral: {
                id: null,
                name: null,
                previewImageUrl: null,
                url: null,
                categories: [],
                clientId: null,
            },

            step: null,

            steps: {
                edit: {
                    title: 'Edit Collateral',
                    buttonText: 'Save',
                    action: this.save,
                    loading: false,
                },

                saving: {
                    title: 'Saving Collateral',
                    buttonText: 'Saving',
                    action: () => {},
                    loading: true,
                },
            },
        };
    },

    computed: {
        accountId() {
            return this.$store.state.context.account.id;
        },
    },

    created() {
        this.step = 'edit';
        this.collateral.id = this.widget.options.collateralId;

        this.getCollateral()
            .catch((error) => {
                this.showFailNotification('An error occurred while fetching your collateral.', { error });
            })
            .finally(() => {
                this.isInitializing = false;
            });
    },

    methods: {
        addListCategories(item) {
            const exist = this.collateral.categories.some((element) => element === item.name);

            if (!exist) {
                this.collateral.categories.push(item.name);
            }
        },

        deleteCategory(item) {
            this.collateral.categories.splice(this.collateral.categories.indexOf(item), 1);
        },

        getCollateral() {
            return new Promise((resolve, reject) => {
                const expand = '$expand=Categories';
                const filter = `$filter=Id eq ${this.collateral.id}`;
                const queryParams = `${expand}&${filter}`;
                this.$api
                    .get(`/service/Collaterals?${queryParams}`)
                    .then(({ data: { value } }) => {
                        const [response] = value;
                        this.collateral = {
                            ...this.collateral,
                            previewImageUrl: response.PreviewImageUrl,
                            url: response.Url,
                            fileExtension: response.FileExtension,
                            fileType: (response.FileType || '').toLowerCase(),
                            name: this.$helpers.getFilenameWithoutExtension(
                                response.Name,
                                response.FileExtension
                            ),
                            clientId: response.ClientId,
                            categories: response.Categories.map(({ Name }) => Name),
                        };
                        resolve();
                    })
                    .catch(reject);
            });
        },

        save() {
            this.step = 'saving';

            this.saveCollateral()
                .then(() => {
                    EventBus.$emit('collateral:update');
                    this.$helpers.showSuccessNotification('Your collateral has been updated.');
                    this.closeWidget();
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while saving your collateral.',
                        { error },
                    );
                });
        },

        saveCollateral() {
            var name = this.collateral.fileExtension
                ? `${this.collateral.name}.${this.collateral.fileExtension}`
                : this.collateral.name;
            const { categories } = this.collateral;

            var changes = [
                { name: 'Name', value: name },
                { name: 'Categories', value: categories },
            ];

            return this.$api.patch(`/api/Collaterals/${this.collateral.id}`, changes);
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },
    },
};
</script>

<style>
.collateral-edit-widget .form-row {
    margin-bottom: 20px;
}

.collateral-edit-widget .preview-image {
    display: block;
    max-width: 100%;
    max-height: 300px;
    margin: 0 auto;
}

.collateral-edit-widget .app-file-icon {
    max-width: 100px;
    margin: 0 auto;
}

.collateral-edit-widget label {
    font-size: 12px;
    color: #877d88;
}

.collateral-edit-widget .action-container {
    display: flex;
}

.collateral-edit-widget .action-container .btn {
    margin-left: 15px;
}

.collateral-edit-widget .cancel-container .btn.transparent-btn {
    background: transparent;
    color: #877d88;
}

.collateral-edit-widget .cancel-container .btn.transparent-btn:hover {
    background: #e1e0e5;
}

.collateral-edit-widget .action-container .btn.white-btn {
    background: #ffffff;
    color: #877d88;
    border: 2px solid #e9e9ef;
}

.collateral-edit-widget .collateral-category-dropdown {
    margin-bottom: 10px;
}

.collateral-edit-widget .categories {
    display: flex;
    flex-wrap: wrap;
    margin: 0 -7px;
}

.collateral-edit-widget .categories .category {
    background: #877d88;
    color: #ffffff;
    font-size: 11px;
    padding: 0px 10px;
    border-radius: 15px;
    margin: 0 7px 9px 7px;
    display: flex;
    align-items: center;
}

.collateral-edit-widget .categories .category svg {
    margin-left: 12px;
    width: 7px;
    cursor: pointer;
}

.collateral-edit-widget .categories .category svg path {
    fill: #ffffff;
}
</style>
